import { ApolloProvider, useMutation } from '@apollo/client'
import { ReactKeycloakProvider, useKeycloak } from '@react-keycloak/web'
import { GraphQLErrorExtensions } from 'graphql'
import React, { useEffect, useState } from 'react'
import client from '../../utils/apolloClient'
import { keycloak as keycloakClient, keycloakProviderProps } from '../../utils/keycloakClient'
import ErrorMessage from '../ErrorMessage'
import Loading from '../Loading'
import { ME } from './mutation'

interface ErrorExtension extends GraphQLErrorExtensions {
  code: string
  serviceName: string
  exception: {
    response: string
    status: number
    message: string
    name: string
  }
}

interface IProps {
  children: React.ReactElement
}

interface attributeElement {
  key: string
  value: any
}

type attribute = {
  [key: string]: any
}

export const customLogin = (redirectUrl: string) => {
  const pathname =
    redirectUrl || (window.location.pathname ? `${window.location.pathname}${window.location.search}` : '/')
  const redirectUri = new URL(`${window?.location?.origin}${pathname}`)
  redirectUri.searchParams.set('login', 'true')
  keycloakClient.login({
    redirectUri: redirectUri.href,
  })
}

function KeycloakCheker({ children }: IProps) {
  const { initialized } = useKeycloak()

  if (!initialized) {
    return <Loading />
  }

  return children
}

function KeycloakLoader({ children }: IProps) {
  const [errorMessage, setErrorMessage] = useState('')

  const { keycloak, initialized } = useKeycloak()

  const [meMutation, { data, loading, error }] = useMutation(ME, {
    onError(error) {
      const exceptionName = (error.graphQLErrors[0]?.extensions as ErrorExtension)?.exception?.name
      if (exceptionName !== 'AuthException') {
        setErrorMessage('Something went wrong! Please try again later')
      }
      console.error('onError meMutation', error)
    },
    onCompleted(data) {
      const dataLayer = (window as any).dataLayer
      if (data?.me?.__typename === 'User') {
        localStorage.setItem('id', JSON.stringify(data?.me?.id))

        if (dataLayer) {
          const attributesHubspot:attribute = {}
          data?.me?.attributes?.hubspot?.forEach((element: attributeElement) => {
            attributesHubspot[element.key] = element.value
          })

          dataLayer.push({
            event: 'userIsAuthenticated',
            email: data?.me?.email,
            id: data?.me?.id,
            role: data?.me?.role,
            name: data?.me?.name,
            companyName: data?.me?.role === 25 ? 'Guest' : data?.me?.company.name,
            companyType: data?.me?.company?.companyType?.name,
            jobTitle: data?.me?.jobTitleType?.name,
            country: data?.me?.country,
            contractPlan: data?.me?.company?.plan?.name,
            releaseType: data?.me?.releaseType,
            companyId: data?.me?.company?.id,
            createdAt: data?.me?.createdAt,
            shouldShowNPS: attributesHubspot?.should_show_nps,
          })
          dataLayer.push({
            event: 'initializeIntercom',
          })
          if (data?.me?.email && !data?.me?.email.includes('@winnin.com')) {
            dataLayer.push({
              event: 'notWinninUserIsAuthenticated',
            })
          }
        }
      }

      if (data?.me?.__typename === 'StatusMessage') {
        setErrorMessage(data?.me?.message)
      }
    },
  })

  useEffect(() => {
    
    keycloak.onAuthRefreshSuccess = () => {
      const isMultisession = keycloak.tokenParsed?.multisession
      if (isMultisession) {
        location.reload()
      }
    }

  }, [keycloak])

  useEffect(() => {
    if (keycloak.authenticated) {
      meMutation()
    } else {
      const dataLayer = (window as any).dataLayer
      dataLayer?.push({
        event: 'initializeIntercom',
      })
    }
  }, [keycloak, meMutation, initialized])

  const isLoading = loading

  if (error || errorMessage) {
    return <ErrorMessage error={errorMessage || error?.message} />
  }

  return <>{!isLoading ? React.cloneElement(children, { keycloak, me: data }) : <Loading />}</>
}

export default function KeycloakWrapper({ children }: IProps) {
  return (
    <ReactKeycloakProvider {...keycloakProviderProps}>
      <ApolloProvider client={client}>
        <KeycloakCheker>
          <KeycloakLoader>{children}</KeycloakLoader>
        </KeycloakCheker>
      </ApolloProvider>
    </ReactKeycloakProvider>
  )
}
